// ตรรกะ pure ของกรอบวิดีโอ (ไม่มี React) แยกจาก useCellSession ให้ทดสอบตรง ๆ ได้

import { CELL } from './constants'
import { isApiError } from './livestreamApi'

/** ขั้นภายใน useDirectSession */
export const PHASE = {
  IDLE: 'idle',
  DISCOVERING: 'discovering',
  CREATING: 'creating',
  POLLING: 'polling',
  READY: 'ready',
  FAILED: 'failed',
  ENDED: 'ended',
}

/** สิ่งที่ player รายงานกลับมา */
export const PLAYER = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  PLAYING: 'playing',
  STALLED: 'stalled',
  ERROR: 'error',
  BLOCKED: 'blocked', // เบราว์เซอร์ไม่ยอมเล่นอัตโนมัติ — ต้องให้ผู้ใช้กดเล่นเอง
}

/** error code ที่แปลว่า "อุปกรณ์ไม่ส่งภาพ" ไม่ใช่ระบบพัง */
const OFFLINE_CODES = ['device_offline', 'device_no_video', 'no_first_frame']

// โหลดไฟล์ไม่ได้ (token หมดอายุ / 403 / 404) ขอ URL ใหม่แล้วอาจหาย
// ส่วนถอดรหัสไม่ได้ / ไม่รองรับ ขอใหม่ก็ไม่ช่วย
const URL_ERROR_CODES = ['networkError', 'NetworkError', 'source_failed']

export function canRefreshFor(code) {
  return URL_ERROR_CODES.indexOf(code) !== -1
}

export function isOfflineCode(error) {
  return !!error && OFFLINE_CODES.indexOf(error.code) !== -1
}

export function toCellError(err, fallbackCode = 'error') {
  if (!err)
    return {
      code: fallbackCode,
      detail: null,
      requestId: null,
      retryable: null,
    }
  if (typeof err === 'string') {
    return { code: err, detail: null, requestId: null, retryable: null }
  }
  if (isApiError(err)) {
    return {
      code: err.code || fallbackCode,
      detail: err.message !== err.code ? err.message : null,
      requestId: err.requestId || null,
      retryable: typeof err.retryable === 'boolean' ? err.retryable : null,
    }
  }
  // problem object จาก state: failed ของ backend ({ code, message, retryable })
  if (err.code) {
    return {
      code: err.code,
      detail: err.message || err.detail || null,
      requestId: err.request_id || err.requestId || null,
      retryable: typeof err.retryable === 'boolean' ? err.retryable : null,
    }
  }
  return {
    code: fallbackCode,
    detail: err.message || null,
    requestId: null,
    retryable: null,
  }
}

// ค่าที่ไม่อยู่ในรายการแล้ว (รถเปลี่ยนกล่อง) ถูกข้าม · null = ไม่มีกล้องเลย
export function pickCamera(discovery, wanted, remembered) {
  const cams = (discovery && discovery.cameras) || []
  if (!cams.length) return null
  const has = n =>
    n !== null && n !== undefined && cams.some(c => c.camera === n)
  if (has(wanted)) return wanted
  if (has(remembered)) return remembered
  return cams[0].camera
}

// เปิดทุกกล้องพร้อมกันแบบหน้าเดิม ยกเว้นกล่องที่เปิดได้ทีละกล้อง (J40 เฟิร์มแวร์ต่ำกว่า 4.3)
// และกล่องที่ไม่ได้ออนไลน์แน่ ๆ (กรอบเดียวบอกสถานะพอ) · null = กรอบเดียวที่เลือกกล้องได้
export function camerasToOpenTogether(discovery) {
  if (!discovery || discovery.online !== true) return null
  if (discovery.one_camera_at_a_time) return null
  const cams = discovery.cameras || []
  return cams.length > 1 ? cams : null
}

// สถานะของกรอบยิงตรง · i.timedOut = ได้ URL แล้วไม่มีภาพจนหมดเวลา
export function deriveCellState(i) {
  if (!i.hasVehicle) return CELL.EMPTY

  switch (i.phase) {
    case PHASE.IDLE:
    case PHASE.DISCOVERING:
    case PHASE.CREATING:
      return CELL.STARTING
    case PHASE.POLLING:
      return CELL.CONNECTING
    case PHASE.FAILED:
      return isOfflineCode(i.directError) ? CELL.OFFLINE : CELL.ERROR
    case PHASE.ENDED:
      return CELL.STOPPED
    case PHASE.READY: {
      // backend บอก ready แต่ไม่มีภาพ: discovery ยืนยันว่ากล่องออนไลน์ = อุปกรณ์ไม่ส่งภาพ
      // vendor บอกสถานะกล่องไม่ได้ (null / ไม่ส่งมา) = เราพิสูจน์ไม่ได้ → ไม่ทราบ
      const unproven = i.deviceOnline === null || i.deviceOnline === undefined
      return fromPlayer(i, unproven ? CELL.UNKNOWN : CELL.OFFLINE)
    }
    default:
      return CELL.UNKNOWN
  }
}

function fromPlayer(i, whenTimedOut) {
  switch (i.player) {
    case PLAYER.PLAYING:
      return CELL.PLAYING
    case PLAYER.STALLED:
      return CELL.STALLED
    case PLAYER.ERROR:
    case PLAYER.BLOCKED:
      return CELL.ERROR
    default:
      return i.timedOut ? whenTimedOut : CELL.CONNECTING
  }
}
